<script lang="ts">
  // Medication swimlanes: one row per drug, a bar per dose segment, hover for dose
  // and dates. Shares the time domain with the measure charts above it so dose
  // changes line up visually with the labs they were meant to move.
  import type { MedicationCourse } from '../types.ts';
  import { fmtMonth, SERIES } from '../format.ts';

  let { courses, domain, eras = [] }: { courses: MedicationCourse[]; domain: [number, number]; eras?: { start: number; end: number | null; label: string }[] } = $props();
  let width = $state(640);
  const L = 168, R = 16, ROW = 30, TOP = 20;
  const innerW = $derived(Math.max(10, width - L - R));
  const height = $derived(TOP + courses.length * ROW + 24);
  const x = (t: number) => L + ((t - domain[0]) / Math.max(1, domain[1] - domain[0])) * innerW;
  const ms = (s: string | null) => (s ? Date.parse(s) : domain[1]);
  let hover = $state<{ x: number; y: number; drug: string; dose: string; start: string; end: string | null } | null>(null);
  const years = $derived.by(() => { const out: number[] = []; for (let y = new Date(domain[0]).getUTCFullYear() + 1; y <= new Date(domain[1]).getUTCFullYear(); y += 2) out.push(y); return out; });
</script>

<div class="wrap" bind:clientWidth={width}>
  <svg {width} {height} role="img" aria-label="Medication courses over time">
    {#each eras as e, i (e.label)}
      {@const x0 = Math.max(L, x(Math.max(e.start, domain[0])))}
      {@const x1 = Math.min(L + innerW, x(e.end == null ? domain[1] : e.end))}
      {#if x1 > x0}<rect x={x0} y={TOP - 14} width={x1 - x0} height={height - TOP + 14 - 22} fill={i % 2 ? 'var(--surface-2)' : 'transparent'} opacity="0.7" /><text x={x0 + 4} y={TOP - 4} class="era">{e.label}</text>{/if}
    {/each}
    {#each years as yr (yr)}
      <line x1={x(Date.UTC(yr, 0, 1))} x2={x(Date.UTC(yr, 0, 1))} y1={TOP} y2={TOP + courses.length * ROW} class="grid" />
      <text x={x(Date.UTC(yr, 0, 1))} y={height - 6} class="tick" text-anchor="middle">{yr}</text>
    {/each}
    {#each courses as c, i (c.drug)}
      {@const cy = TOP + i * ROW + ROW / 2}
      <text x={L - 10} y={cy - 2} class="drug" text-anchor="end">{c.drug}</text>
      <text x={L - 10} y={cy + 9} class="cls" text-anchor="end">{c.class}</text>
      {#each c.segments as s, j (s.start + j)}
        {@const x0 = x(ms(s.start))}
        {@const x1 = x(ms(s.end))}
        {@const w = Math.max(2, x1 - x0 - 2)}
        <g role="presentation" onmouseenter={(e) => { const r = (e.currentTarget as SVGGElement).ownerSVGElement!.getBoundingClientRect(); hover = { x: Math.min(x0 + w / 2, width - 180), y: cy, drug: c.drug, dose: s.dose, start: s.start, end: s.end }; void r; }} onmouseleave={() => (hover = null)}>
          <rect x={x0} y={cy - 9} width={w} height="18" rx="4" fill={SERIES[i % SERIES.length]} opacity={s.end == null ? 1 : 0.85} />
          {#if w > s.dose.length * 6.5 + 10}<text x={x0 + w / 2} y={cy + 3.5} class="dose" text-anchor="middle">{s.dose}</text>{/if}
        </g>
      {/each}
      {#if c.end}<text x={x(ms(c.end)) + 6} y={cy + 3.5} class="stopped">stopped {fmtMonth(c.end)}</text>{/if}
    {/each}
  </svg>
  {#if hover}
    <div class="tip" style="left:{hover.x}px; top:{hover.y + 14}px">
      <b>{hover.drug}</b> · {hover.dose}<br /><span>{fmtMonth(hover.start)} → {hover.end ? fmtMonth(hover.end) : 'current'}</span>
    </div>
  {/if}
</div>

<style>
  .wrap { position: relative; width: 100%; }
  svg { display: block; overflow: visible; }
  .grid { stroke: var(--line); stroke-width: 1; }
  .tick, .era, .cls, .stopped { font-size: 10.5px; fill: var(--text-3); font-family: var(--font); }
  .era { font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; }
  .drug { font-size: 12px; fill: var(--text); font-weight: 600; font-family: var(--font); }
  .dose { font-size: 10.5px; fill: #fff; font-weight: 700; font-family: var(--font); }
  .tip { position: absolute; transform: translateX(-50%); pointer-events: none; background: var(--surface); border: 1px solid var(--line-strong); border-radius: var(--radius-sm); padding: 6px 10px; font-size: var(--fs-foot); color: var(--text-2); box-shadow: 0 4px 16px rgba(15, 20, 26, 0.12); white-space: nowrap; z-index: 2; }
  .tip b { color: var(--text); }
</style>
